import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Globe, Plus, Trash2, Check, AlertTriangle, Loader2, X } from "lucide-react";
import { useSiteContext } from "../contexts/SiteContext";
import { normalizeDomain, type UserSite } from "../services/siteService";

const ACCENT = "var(--accent, #8B5CF6)";

function formatTimeAgo(isoDate?: string): string {
  if (!isoDate) return "No audits yet";
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

interface SiteSwitcherProps {
  onSiteChange?: (domain: string) => void;
}

const SiteSwitcher: React.FC<SiteSwitcherProps> = ({ onSiteChange }) => {
  const { sites, activeSite, loading, switchSite, addNewSite, removeSite } = useSiteContext();
  const [isOpen, setIsOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowAddForm(false);
        setError(null);
        setConfirmDelete(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSwitchSite = async (site: UserSite) => {
    await switchSite(site);
    setIsOpen(false);
    onSiteChange?.(site.domain);
  };

  const handleAddSite = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    setError(null);
    const cleanDomain = normalizeDomain(newUrl.trim());
    const result = await addNewSite(cleanDomain);
    if (result.success) {
      setNewUrl("");
      setShowAddForm(false);
      setIsOpen(false);
      onSiteChange?.(cleanDomain);
    } else {
      setError(result.message || "Failed to add site");
    }
    setAdding(false);
  };

  const handleDeleteSite = async (site: UserSite) => {
    if (confirmDelete !== site.id) {
      setConfirmDelete(site.id);
      return;
    }
    await removeSite(site);
    setConfirmDelete(null);
    if (sites.length <= 1) setIsOpen(false);
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 rounded-[10px] border border-white/[0.07] bg-og01-card px-3 py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: ACCENT }} />
        <span className="text-xs text-og01-text-secondary">Loading sites…</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex max-w-[260px] items-center gap-2 rounded-[10px] border border-white/[0.07] px-3 py-2 transition-colors hover:border-white/[0.12]"
        style={{ background: isOpen ? "#0F0F18" : "transparent" }}
      >
        <Globe className="h-4 w-4 flex-shrink-0" style={{ color: ACCENT }} />
        <span className="truncate text-sm font-medium text-og01-text">{activeSite?.domain || "Select a site"}</span>
        <ChevronDown
          className="h-3.5 w-3.5 flex-shrink-0 text-og01-text-muted transition-transform"
          style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 top-full z-50 mt-2 w-[320px] overflow-hidden rounded-xl border border-white/[0.08]"
          style={{
            background: "rgba(15,15,24,0.98)",
            boxShadow: "0 24px 60px -20px rgba(0,0,0,.7)",
            backdropFilter: "blur(12px)",
          }}
        >
          {/* Header */}
          <div className="px-4 pb-2 pt-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-og01-text-muted">Your Sites</div>
          </div>

          {/* Site list */}
          <div className="max-h-[280px] overflow-y-auto px-2 pb-2">
            {sites.length > 0 ? (
              sites.map((site) => (
                <div
                  key={site.id}
                  className="group flex items-center gap-2 rounded-lg px-2 py-2 transition-colors hover:bg-white/5"
                  style={{ background: site.isActive ? `color-mix(in srgb, ${ACCENT} 10%, transparent)` : "transparent" }}
                >
                  <button onClick={() => handleSwitchSite(site)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <div
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border"
                      style={{
                        background: site.isActive ? `color-mix(in srgb, ${ACCENT} 18%, transparent)` : "rgba(255,255,255,0.04)",
                        borderColor: site.isActive ? `color-mix(in srgb, ${ACCENT} 32%, transparent)` : "rgba(255,255,255,0.07)",
                      }}
                    >
                      {site.isActive ? (
                        <Check className="h-3.5 w-3.5" style={{ color: ACCENT }} />
                      ) : (
                        <Globe className="h-3.5 w-3.5 text-og01-text-muted" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-sm font-medium ${site.isActive ? "text-og01-text" : "text-og01-text-secondary"}`}>
                        {site.domain}
                      </div>
                      <div className="text-[10px] text-og01-text-muted">
                        {site.auditCount ?? 0} audits · Last: {formatTimeAgo(site.lastAuditAt)}
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSite(site);
                    }}
                    className="flex-shrink-0 rounded-lg p-1.5 opacity-0 transition-all hover:bg-red-500/10 group-hover:opacity-100"
                    style={{ color: confirmDelete === site.id ? "#F87171" : "#6B6585" }}
                    title={confirmDelete === site.id ? "Click again to confirm deletion" : "Delete site and all data"}
                  >
                    {confirmDelete === site.id ? <AlertTriangle className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              ))
            ) : (
              <div className="py-6 text-center">
                <Globe className="mx-auto mb-2 h-8 w-8 text-og01-text-muted" />
                <div className="text-xs text-og01-text-secondary">No sites yet</div>
                <div className="mt-0.5 text-[10px] text-og01-text-muted">Add a site below to run its first audit</div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="mx-3 border-t border-white/[0.07]" />

          {/* Add new site */}
          {showAddForm || adding ? (
            <div className="space-y-2 p-3">
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  placeholder="https://yoursite.com"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddSite()}
                  className="flex-1 rounded-lg border border-white/[0.07] bg-[#0F0F18] px-3 py-2 text-sm text-og01-text outline-none transition-colors focus:border-white/[0.14] placeholder:text-og01-text-muted"
                  autoFocus
                />
                <button
                  onClick={handleAddSite}
                  disabled={adding || !newUrl.trim()}
                  className="rounded-lg px-3 py-2 text-xs font-semibold text-white transition-all hover:brightness-110 disabled:opacity-40"
                  style={{ background: ACCENT }}
                  title="We'll run the audit for this site"
                >
                  {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add & Audit"}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setError(null);
                  }}
                  className="rounded-lg p-1.5 text-og01-text-muted hover:bg-white/10"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {error && <div className="px-1 text-[10px]" style={{ color: "#F87171" }}>{error}</div>}
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
              style={{ color: ACCENT }}
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">Add New Site</span>
            </button>
          )}

          {/* Footer — site count */}
          <div className="border-t border-white/[0.07] px-4 py-2 text-center">
            <span className="text-[10px] text-og01-text-muted">
              {sites.length} site{sites.length !== 1 ? "s" : ""} tracked
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSwitcher;
